import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { Reveal } from '../ui-custom/Reveal';
import { theme } from '../../utils/theme';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const TestimonialSection = () => {
  const { language } = useLanguage();
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        // Fallback data
        setTestimonials([
          {
            name: "Sophie L.",
            role: "Architecte à Lyon",
            role_en: "Architect in Lyon",
            avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Sophie",
            rating: 5,
            text: "J'ai réduit ma facture de clim de 25% cet été. Mais le plus fou, c'est que je n'ai plus besoin de vivre dans le noir.",
            text_en: "I reduced my AC bill by 25% this summer. But the craziest thing is that I no longer need to live in the dark."
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    if (testimonials.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [testimonials.length]);

  const current = testimonials[currentIndex];

  // Get text based on language
  const getText = (field) => {
    if (!current) return '';
    if (language === 'en') {
      return current[`${field}_en`] || current[field];
    }
    return current[field];
  };

  if (loading || !current) {
    return (
      <section className="py-24 border-t relative overflow-hidden" style={{ borderColor: theme.colors.border }}>
        <div className="flex justify-center py-12">
          <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 border-t relative overflow-hidden"
      style={{ borderColor: theme.colors.border }}
    >
      <div 
        className="absolute inset-0 opacity-20" 
        style={{ 
          backgroundImage: 'radial-gradient(#333 1px, transparent 1px)', 
          backgroundSize: '20px 20px' 
        }}
      />
      
      <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
        <Reveal>
          <div className="flex justify-center mb-8 space-x-1">
            {[...Array(current.rating || 5)].map((_, i) => (
              <span 
                key={i} 
                className="text-orange-400 text-2xl animate-pulse" 
                style={{ animationDelay: `${i * 100}ms` }}
              >
                ★
              </span>
            ))}
          </div>
          
          <blockquote className="text-3xl md:text-4xl font-light italic mb-10 text-white leading-tight">
            "{getText('text')}"
          </blockquote>
          
          <div className="flex items-center justify-center gap-4">
            <div className="w-12 h-12 bg-gray-800 rounded-full overflow-hidden">
              <img 
                src={current.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${current.name}`} 
                alt={`Avatar ${current.name}`} 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-left">
              <div className="font-bold text-white">{current.name}</div>
              <div className="text-sm text-gray-500">{getText('role')}</div>
            </div>
          </div>

          {/* Pagination dots */}
          {testimonials.length > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentIndex ? 'bg-orange-500 w-6' : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                  aria-label={`Testimonial ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
};
